import { useSyncExternalStore } from 'react';

/**
 * Thème de l'application : clair, sombre ou celui du système (par défaut). Préférence retenue dans le navigateur ;
 * appliquée par la classe `dark` de <html> (variante `dark:` de Tailwind, palettes sombres de src/styles/dark.css).
 * Appliquée une première fois avant le rendu par public/theme-init.js (même règle), pour éviter un flash clair.
 */
export type ThemePreference = 'light' | 'dark' | 'system';

export const THEME_STORAGE_KEY = 'teiji.theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

const listeners = new Set<() => void>();

export function getThemePreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'system';
  } catch {
    return 'system';
  }
}

export function setThemePreference(preference: ThemePreference): void {
  try {
    if (preference === 'system') localStorage.removeItem(THEME_STORAGE_KEY);
    else localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Stockage indisponible : le choix vaut jusqu'au rechargement
  }
  applyTheme(preference);
  listeners.forEach((listener) => listener());
}

/** Sombre ou non, selon la préférence (et le système pour « system ») */
export function isDarkTheme(preference: ThemePreference = getThemePreference()): boolean {
  return preference === 'dark' || (preference === 'system' && window.matchMedia(DARK_QUERY).matches);
}

export function applyTheme(preference: ThemePreference = getThemePreference()): void {
  document.documentElement.classList.toggle('dark', isDarkTheme(preference));
}

/** Suit le thème du système tant que la préférence est « system » ; à appeler une fois au démarrage */
export function watchSystemTheme(): () => void {
  const media = window.matchMedia(DARK_QUERY);
  const onChange = () => {
    if (getThemePreference() === 'system') applyTheme('system');
    listeners.forEach((listener) => listener());
  };
  media.addEventListener('change', onChange);
  return () => media.removeEventListener('change', onChange);
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Préférence courante, et le thème effectivement affiché (pour les composants qui en dépendent, ex. les toasts) */
export function useTheme(): { preference: ThemePreference; dark: boolean } {
  const preference = useSyncExternalStore(subscribe, getThemePreference);
  const dark = useSyncExternalStore(subscribe, () => isDarkTheme(preference));
  return { preference, dark };
}
